import React, { useState, useEffect } from 'react';
import { Check, Bot } from 'lucide-react';
import { Badge } from './ds';

const FONT = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const QuestionCard = ({ question, answer, onAnswerChange, onOpenAi }) => {
  const [localAnswer, setLocalAnswer] = useState(answer?.answer || '');

  useEffect(() => {
    setLocalAnswer(answer?.answer || '');
  }, [answer]);

  const handleChange = (value) => {
    setLocalAnswer(value);
    onAnswerChange(value);
  };

  const isDin = !!question.dinNr;
  const isStatusQuery = question.isStatusQuery;

  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '28px 32px',
      border: '1px solid #D7DCE4',
      boxShadow: '0 1px 3px rgba(16,22,35,0.06), 0 1px 2px rgba(16,22,35,0.04)',
      fontFamily: FONT,
    }}>

      {/* Header row: category + DIN nr */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <Badge tone={isStatusQuery ? 'neutral' : 'info'}>{question.category}</Badge>

        {isDin && (
          <span style={{
            display: 'inline-block', padding: '4px 8px', borderRadius: 6,
            background: '#F5F7FA', border: '1px solid #D7DCE4',
            color: '#667085', fontSize: 11, fontWeight: 600,
          }}>Nr. {question.dinNr}</span>
        )}

        {isStatusQuery && (
          <Badge tone="neutral">Statusabfrage</Badge>
        )}
      </div>

      <h3 style={{
        margin: '0 0 24px', fontSize: 16, fontWeight: 600,
        color: '#101623', lineHeight: 1.6,
      }}>{question.text}</h3>

      {/* Yes / No buttons */}
      {question.type === 'yes_no' && (
        <div style={{ display: 'flex', gap: 12 }}>
          {[
            { value: 'yes', label: 'Ja' },
            { value: 'no',  label: 'Nein' },
          ].map(opt => {
            const sel = localAnswer === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => handleChange(opt.value)}
                style={{
                  flex: 1, padding: '14px 20px',
                  border: `2px solid ${sel ? '#101623' : '#D7DCE4'}`,
                  borderRadius: 8, cursor: 'pointer', fontFamily: FONT,
                  background: sel ? '#EBEEF3' : '#F5F7FA',
                  color: sel ? '#101623' : '#475569',
                  fontWeight: sel ? 700 : 500, fontSize: 15,
                  transition: 'all 120ms',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                }}
              >
                <span style={{
                  width: 22, height: 22, borderRadius: '50%',
                  border: `2px solid ${sel ? '#101623' : '#B6BDC9'}`,
                  background: sel ? '#101623' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {sel && <Check width={12} height={12} color="#fff" strokeWidth={3} />}
                </span>
                {opt.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Multiple choice */}
      {question.type === 'multiple_choice' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {question.options?.map((option, idx) => {
            const sel = localAnswer === option;
            return (
              <label key={idx} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 16px', borderRadius: 8, cursor: 'pointer',
                border: `2px solid ${sel ? '#1E4FBE' : '#D7DCE4'}`,
                background: sel ? '#F2F6FE' : '#F5F7FA',
                transition: 'all 120ms',
              }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${sel ? '#1E4FBE' : '#B6BDC9'}`,
                  background: sel ? '#1E4FBE' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {sel && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                </div>
                <input
                  type="radio" name={`q_${question.id}`} value={option}
                  checked={sel} onChange={e => handleChange(e.target.value)}
                  style={{ display: 'none' }}
                />
                <span style={{ fontSize: 14, color: sel ? '#1B4098' : '#374151', fontWeight: sel ? 600 : 400 }}>
                  {option}
                </span>
              </label>
            );
          })}
        </div>
      )}

      {/* Rating */}
      {question.type === 'rating' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            {[1, 2, 3, 4, 5].map(star => {
              const active = parseInt(localAnswer) >= star;
              return (
                <button key={star} onClick={() => handleChange(star.toString())} style={{
                  width: 46, height: 46,
                  border: `2px solid ${active ? '#D98A1A' : '#D7DCE4'}`,
                  borderRadius: 8, cursor: 'pointer',
                  background: active ? '#FDF6EA' : '#F5F7FA',
                  color: active ? '#B4690E' : '#D7DCE4',
                  fontSize: 22, transition: 'all 120ms',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>★</button>
              );
            })}
          </div>
        </div>
      )}

      {/* AI assistant trigger */}
      {onOpenAi && (
        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <button
            onClick={onOpenAi}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: 'none', border: '1px solid #D7DCE4', minHeight: 44,
              borderRadius: 999, padding: '6px 16px',
              cursor: 'pointer', fontFamily: FONT,
              fontSize: 12, color: '#667085',
              transition: 'all 120ms',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#1E4FBE'; e.currentTarget.style.color = '#1B4098'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#D7DCE4'; e.currentTarget.style.color = '#667085'; }}
          >
            <Bot width={14} height={14} />
            Frage an KI stellen
          </button>
        </div>
      )}

    </div>
  );
};

export default QuestionCard;
